import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Departments allowed to see "สร้าง Sale Order". Matched against
// currentUser.department (the erp_department string from SSO, e.g.
// "M180101 แผนกประสานงานขาย"). Add the exact ERP code(s) here.


const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [fifoOpen, setFifoOpen] = useState(false);
  const location = useLocation();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  // Auto-open FIFO group when on a FIFO page
  useEffect(() => {
    const fifoRoutes = ['/request-machine', '/inventory-queue', '/qc-queue', '/qc-history', '/all-requests', '/request-detail'];
    if (fifoRoutes.some(r => location.pathname.startsWith(r))) {
      setFifoOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const close = e => { if (e.key === 'Escape') { document.querySelector('.sidebar')?.classList.remove('sidebar--mobile-open'); document.body.classList.remove('sidebar-mobile-open'); } };
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('keydown', close); document.body.classList.remove('sidebar-mobile-open'); };
  }, []);

  if (!currentUser || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  // inventory-queue/qc-queue are role-restricted (warehouse/qc respectively,
  // matching backend's requireRole()) — request-machine stays open to
  // everyone. userRoles empty (no role assigned in SSO yet) hides both,
  // same fail-closed behavior as the route guard itself.
  const userRoles = currentUser?.roles || [];
  const allFifoItems = [
    { path: '/request-machine', icon: 'cart-plus-fill', label_th: 'เบิกเครื่อง' },
    { path: '/inventory-queue', icon: 'box-seam-fill', label_th: 'คลังสินค้า', requiresRole: 'warehouse' },
    { path: '/qc-queue', icon: 'shield-check-fill', label_th: 'ตรวจสอบคุณภาพ', requiresRole: 'qc' },
    { path: '/qc-history', icon: 'clipboard-data-fill', label_th: 'รายงาน QC', requiresRole: 'qc' },
  ];
  const fifoItems = allFifoItems.filter(item => !item.requiresRole || userRoles.includes(item.requiresRole));

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isFifoActive = fifoItems.some(item => isActive(item.path));

  // TEMP: visible to every logged-in user. To restrict later, set this back to:
  //   !!currentUser?.department && CREATE_SO_DEPT_CODES.some(code => currentUser.department.includes(code))
  const canCreateSO = !!currentUser;

  const closeMobileSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) sidebar.classList.remove('sidebar--mobile-open');
    document.body.classList.remove('sidebar-mobile-open');
  };

  return (
    <>
    <div className="sidebar-overlay" onClick={closeMobileSidebar}></div>
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
      {/* Logo */}
      <div className="sidebar__header">
        <Link to="/" className="sidebar__logo">
          <i className="bi bi-stack sidebar__logo-icon"></i>
          {!collapsed && <span className="sidebar__logo-text">VERP</span>}
        </Link>
        <button
          className="sidebar__toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`bi bi-chevron-${collapsed ? 'right' : 'left'}`}></i>
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav" onClick={e => { if (e.target.closest('a')) closeMobileSidebar(); }}>
        {!collapsed && <div className="nav-section-label">WORKSPACE</div>}
        <ul className="sidebar__list">

          {/* Dashboard */}
          <li className="sidebar__item">
            <Link
              to="/"
              className={`sidebar__link ${isActive('/') ? 'sidebar__link--active' : ''}`}
              title={collapsed ? 'แดชบอร์ด' : ''}
            >
              <i className="bi bi-grid-fill sidebar__icon"></i>
              {!collapsed && <span className="sidebar__label">แดชบอร์ด</span>}
            </Link>
          </li>

          {/* FIFO Group */}
          <li className="sidebar__item">
            <button
              className={`sidebar__link sidebar__link--group ${isFifoActive ? 'sidebar__link--active' : ''}`}
              onClick={() => !collapsed && setFifoOpen(o => !o)}
              title={collapsed ? 'FIFO' : ''}
            >
              <i className="bi bi-arrow-repeat sidebar__icon"></i>
              {!collapsed && (
                <>
                  <span className="sidebar__label">FIFO</span>
                  <i className={`bi bi-chevron-${fifoOpen ? 'down' : 'right'} sidebar__chevron`}></i>
                </>
              )}
            </button>

            {(fifoOpen || collapsed) && (
              <ul className={`sidebar__submenu ${collapsed ? 'sidebar__submenu--collapsed' : ''}`}>
                {fifoItems.map(item => (
                  <li key={item.path} className="sidebar__subitem">
                    <Link
                      to={item.path}
                      className={`sidebar__sublink ${isActive(item.path) ? 'sidebar__sublink--active' : ''}`}
                      title={item.label_th}
                    >
                      <i className={`bi bi-${item.icon} sidebar__icon`}></i>
                      {!collapsed && <span className="sidebar__label">{item.label_th}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Rental Documents */}
          <li className="sidebar__item">
            <Link
              to="/rental-so"
              className={`sidebar__link ${isActive('/rental-so') ? 'sidebar__link--active' : ''}`}
              title={collapsed ? 'เอกสารเครื่องเช่า' : ''}
            >
              <i className="bi bi-file-earmark-text-fill sidebar__icon"></i>
              {!collapsed && <span className="sidebar__label">เอกสารเครื่องเช่า</span>}
            </Link>
          </li>

          {/* What happened to each SO Venio sent (admin: all, sales: own) */}
          <li className="sidebar__item">
            <Link
              to="/webhook-log"
              className={`sidebar__link ${isActive('/webhook-log') ? 'sidebar__link--active' : ''}`}
              title={collapsed ? 'บันทึกการรับ SO' : ''}
            >
              <i className="bi bi-journal-text sidebar__icon"></i>
              {!collapsed && <span className="sidebar__label">บันทึกการรับ SO</span>}
            </Link>
          </li>

          {/* Problem report form for the e-Contract section (external Google Form, opens in a new tab) */}
          <li className="sidebar__item">
            <a
              href="https://forms.gle/3MrDkSwujcgBTDSNA"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar__link"
              title={collapsed ? 'แบบฟอร์มแจ้งปัญหาส่วน E-Contract' : ''}
            >
              <i className="bi bi-flag-fill sidebar__icon"></i>
              {!collapsed && (
                <span className="sidebar__label">
                  แบบฟอร์มแจ้งปัญหาส่วน E-Contract <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.75em', opacity: 0.6 }}></i>
                </span>
              )}
            </a>
          </li>

          {/* Goods-to-asset requisition form (external smart form, opens in a new tab) */}
          <li className="sidebar__item">
            <a
              href="https://apps.peerapat.com/choke/FME/static/smart_form.html?form_id=sf_64e138cc"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar__link"
              title={collapsed ? 'แบบฟอร์มการเบิกสินค้าเป็นทรัพย์สิน' : ''}
            >
              <i className="bi bi-box-arrow-in-right sidebar__icon"></i>
              {!collapsed && (
                <span className="sidebar__label">
                  แบบฟอร์มการเบิกสินค้าเป็นทรัพย์สิน <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.75em', opacity: 0.6 }}></i>
                </span>
              )}
            </a>
          </li>

          {/* Create Sale Order — department-gated */}
          {canCreateSO && (
            <li className="sidebar__item">
              <Link
                to="/create-order"
                className={`sidebar__link ${isActive('/create-order') ? 'sidebar__link--active' : ''}`}
                title={collapsed ? 'สร้าง Sale Order' : ''}
              >
                <i className="bi bi-file-earmark-plus-fill sidebar__icon"></i>
                {!collapsed && <span className="sidebar__label">สร้าง Sale Order</span>}
              </Link>
            </li>
          )}

          {/* Sale Co — conditional */}
          {currentUser?.department?.includes('ประสานงานขาย') && (
            <li className="sidebar__item">
              <Link
                to="/response/1"
                className={`sidebar__link ${isActive('/response') ? 'sidebar__link--active' : ''}`}
                title={collapsed ? 'ประสานงานขาย' : ''}
              >
                <i className="bi bi-people-fill sidebar__icon"></i>
                {!collapsed && <span className="sidebar__label">ประสานงานขาย</span>}
              </Link>
            </li>
          )}

          {/* Help */}
          <li className="sidebar__item">
            <Link
              to="/help"
              className={`sidebar__link ${isActive('/help') ? 'sidebar__link--active' : ''}`}
              title={collapsed ? 'ช่วยเหลือ' : ''}
            >
              <i className="bi bi-question-circle-fill sidebar__icon"></i>
              {!collapsed && <span className="sidebar__label">ช่วยเหลือ</span>}
            </Link>
          </li>

        </ul>
      </nav>

      {/* Footer */}
      <div className="sidebar__footer">
        {!collapsed && (
          <div className="sidebar__mis-info">
            <div className="sidebar__mis-title"><i className="bi bi-headset me-2" />ต้องการความช่วยเหลือ?</div>
            <div className="sidebar__mis-developer">ติดต่อทีม MIS · โทร 136</div>
            <div className="sidebar__mis-version">PEERAPAT WORKSPACE</div>
          </div>
        )}
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
